import {
  InventoryCenter,
  ManufacturingLocation,
  MCell,
  MCellDto,
  PlanningDepartment,
} from '../manufacturingLocation/types';
import { User } from '../profile/types';
import {
  JobFunction,
  JobFunctionDto,
  JobFunctionTagDto,
} from '../mCellEmployee/types';

export type Employee = {
  id: string;
  viewHvrAppsEmployeeKey: string;
  employeeNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  employeeCategoryCode: string;
  organizationName: string;
  departmentCode: string;
  producingLocationCode: string;
  workCenterCode: string;
  accountCode: string;
  crewCode: string;
  teamCode: string;
  payrollName: string;
  lastUpdatedDate: string | null;
  lastUpdatedTime: string | null;
  lastUpdatedTimestamp: string | null;
  badgeNumber: string;
  title: string;
  companyNumber: string;
  supervisorFullName: string;
  supervisorEmployeeNumber: string;
  adUsername: string;
  email: string;
  inventoryCenterCode: string;
  planningDepartmentCode: string;
  active: boolean;
};

export type EmployeeInterest = {
  id: string;
  interest: string;
};

export type EmployeeInterestInfo = {
  interest: string;
};

export type WorkSchedule = 'PartTime' | 'FullTime' | 'Overtime';

export type InterestProfileInfo = {
  mCellEmployeeId: string;
  preferredWorkSchedule: WorkSchedule;
  interestIds: string[];
  crossTrainingDepartmentIds: string[];
};

type InterestProfileEmployeeInterest = {
  employeeInterest: EmployeeInterest;
};

type InterestProfilePlanningDepartment = {
  planningDepartment: PlanningDepartment;
};

export type InterestProfile = {
  id: string;
  mCellEmployee: MCellEmployee;
  preferredWorkSchedule: WorkSchedule;
  interestProfileEmployeeInterest: InterestProfileEmployeeInterest[];
  interestProfilePlanningDepartment: InterestProfilePlanningDepartment[];
};

export type MCellEmployee = {
  id: string;
  employeeId: string;
  fullBadgeId: string;
  homeMCell?: MCell;
  homeMCellId?: string;
  firstName: string;
  lastName: string;
  user?: User;
  userId?: string;
  inventoryCenter: InventoryCenter;
  inventoryCenterId: string;
  supervisorName: string;
  accountType?: MCellEmployeeAccountType;
  accountTypeId?: string;
  category?: MCellEmployeeCategory;
  categoryId?: string;
  role?: MCellEmployeeRole;
  roleId?: string;
  defaultMCellShift: Shift;
  department?: PlanningDepartment;
  departmentId?: string;
  active: boolean;
  isLocal: boolean;
};

export type MCellEmployeeInMCell = MCellEmployee & {
  isLoggedIn: boolean;
  isAssigned: boolean;
  hasHomeMCellInLocation: boolean;
  currentMCell?: MCell;
  currentMCellId?: string;
  isLoggedInToHomeCell?: boolean;
};

export type MCellEmployeeFilterValue = {
  nameContains?: string;
  employeeIdContains?: string;
  inventoryCenterId?: string;
  homeInventoryCenterId?: string;
  homeMCellId?: string;
  homeMCellCollectionId?: string;
  departmentIds?: string[];
  hasExperienceInMCellId?: string;
  hasExperienceInJobStationIds?: string[];
  hasExperienceInJobFunctionIds?: string[];
  hasExperienceInJobFunctionTagIds?: string[];
  defaultShifts?: Shift[];
  filterIsCurrentlyLoggedIn?: boolean;
  departmentOfInterestIds?: string[];
  preferredWorkSchedules?: WorkSchedule[];
  employeeInterestIds?: string[];
  mCellEmployeeAccountTypeIds?: string[];
  mCellEmployeeRoleIds?: string[];
  mCellEmployeeCategoryIds?: string[];
  lmsCompletedCourseTitles?: string[];
  lmsEnrolledCourseTitles?: string[];
  lmsPassedAssessmentIds?: string[];
  lmsEnrolledAssessmentIds?: string[];
};

export type Shift =
  | 'None'
  | 'Day'
  | 'Night'
  | 'Graveyard'
  | 'Weekend'
  | 'Unknown';

export type MCellEmployeeInput = {
  employeeId: string;
  homeMCellId?: string | null;
  defaultMCellShift: Shift;
};

export type MCellStatusLevel = 'None' | 'Red' | 'Yellow' | 'Green';

export type ManufacturingLocationShift = ManufacturingLocation & {
  shift: Shift;
};

export type MCellShiftInfo = {
  mCellId: string;
  shift: Shift;
};

export type MCellCapacity = {
  id: string;
  mCell: MCell;
  minimum: number;
  maximum: number;
  optimum: number;
};

export type MCellStatus = {
  id: string;
  mCell: MCell;
  mCellId: string;
  operationsStatus: MCellStatusLevel;
  operationsStatusBy?: User;
  demandManagementStatus: MCellStatusLevel;
  demandManagementStatusBy?: User;
  demandManagementStatusByAuto: boolean;
};

export type MCellStatusInfo = {
  id: string;
  status: MCellStatusLevel;
};

export type MCellEmployeeMCellHistory = {
  mCellEmployeeId: string;
  mCell: MCell;
  timeMinutes: number;
  lastLoginDate: string;
};

export type JobStationHistoryDto = {
  mCellEmployeeId: string;
  jobStation: JobStationDto;
  timeMinutes: number;
  lastLoginDate: string;
  lastAssignmentDate: string;
};

export type JobFunctionHistoryDto = {
  mCellEmployeeId: string;
  jobFunction: JobFunctionDto;
  timeMinutes: number;
  lastAssignmentDate: string;
};

export type JobFunctionTagHistoryDto = {
  mCellEmployeeId: string;
  jobFunctionTag: JobFunctionTagDto;
  timeMinutes: number;
  lastAssignmentDate: string;
};

export type MCellCapacityInfo = {
  minimum: number;
  maximum: number;
  optimum: number;
};

export type MCellCurrentCapacity = {
  currentEmployeeCount: number;
  percentFromOptimum: number;
  percentFromMinimum: number;
  percentFromMaximum: number;
  mCellCapacity: MCellCapacity;
  mCellStatus: MCellStatus;
};

export type MCellCurrentCapacitiesSummary = {
  currentEmployeeCountSum: number;
  minimumSum: number;
  optimumSum: number;
  maximumSum: number;
  fromMinimumSum: number;
  fromOptimumSum: number;
  fromMaximumSum: number;
  percentFromOptimum: number;
  percentFromMinimum: number;
  percentFromMaximum: number;
};

export type MCellEmployeeAccountType = {
  id: string;
  accountTypeId: string;
  description: string;
};

export type MCellEmployeeRole = {
  id: string;
  title: string;
};

export type MCellEmployeeCategory = {
  id: string;
  categoryId: string;
  description: string;
};

export type DemandManagementStatusInventoryCenterConfig = {
  id: string;
  inventoryCenterId: string;
  redStatusThresholdPercent: number;
  yellowStatusThresholdPercent: number;
};

export type DemandManagementStatusInventoryCenterConfigInfo = {
  inventoryCenterId: string;
  redStatusThresholdPercent: number;
  yellowStatusThresholdPercent: number;
};

export type DemandManagementStatusAutomationMode =
  | 'Disabled'
  | 'EnabledAuto'
  | 'EnabledGreen'
  | 'EnabledYellow'
  | 'EnabledRed';

export type DemandManagementStatusMCellConfig = {
  id: string;
  mCell: MCell;
  mCellId: string;
  automationMode: DemandManagementStatusAutomationMode;
  isCellBoardEnabled: boolean;
  locations: DeliveryMetricLocation[];
};

export type DeliveryMetricLocation = {
  producingLocation?: string;
  productionLine?: string;
  workCenter?: string;
};

export type DemandManagementStatusMCellConfigInfo = {
  mCell: MCell;
  mCellId: string;
  automationMode: DemandManagementStatusAutomationMode;
};

export type DangerousEquipment = {
  id: string;
  description: string;
  inventoryCenter?: InventoryCenter;
  inventoryCenterId?: string;
};

export type PersonalProtectiveEquipment = {
  id: string;
  description: string;
  inventoryCenter?: InventoryCenter;
  inventoryCenterId?: string;
};

export type DangerousEquipmentInfo = {
  id?: string;
  description: string;
  inventoryCenterId: string;
};
export type PersonalProtectiveEquipmentInfo = {
  id?: string;
  description: string;
  inventoryCenterId: string;
};

export const ageRestrictions = ['None', 'Over18'] as const;

export type AgeRestriction = (typeof ageRestrictions)[number];

export const weightRestrictions = [
  'None',
  'Light',
  'Medium',
  'Heavy',
  'VeryHeavy',
] as const;

export type WeightRestriction = (typeof weightRestrictions)[number];

export type JobStationPersonalProtectiveEquipment = {
  personalProtectiveEquipmentId: string;
  personalProtectiveEquipment: Pick<
    PersonalProtectiveEquipment,
    'id' | 'description'
  >;
};

export type JobStationPersonalProtectiveEquipmentInfo = {
  jobStationId: string;
  personalProtectiveEquipmentId: string;
};

export type JobStationDangerousEquipment = {
  dangerousEquipmentId: string;
  dangerousEquipment: Pick<DangerousEquipment, 'id' | 'description'>;
};

export type JobStationDangerousEquipmentInfo = {
  jobStationId: string;
  dangerousEquipmentId: string;
};

export type JobStation = {
  id: string;
  title: string;
  description?: string;
  mCellId: string;
  mCell?: MCellDto;
  jobFunction?: JobFunctionDto;
  jobFunctionId: string;
  stationId: string;
  stationSequence: string;
  positionCount: number;
  crewingRateMaximum: number;
  ageRestriction: AgeRestriction;
  weightRestriction: WeightRestriction;
  /**
   * Raw manual override URL exactly as stored on the row. Populated by every job-station
   * response. Use this in admin/edit forms; use {@link documentationUrlResolved} for display.
   */
  documentationUrlOverride?: string | null;
  /**
   * ASCII / punycode host derived from {@link documentationUrlOverride} for safe display in
   * tooltips/aria-labels. Null when no override is set.
   */
  documentationUrlOverrideHost?: string | null;
  /**
   * Effective documentation URL for end-user display: override if set, otherwise the
   * SharePoint-resolved URL. Populated only by the resolved endpoints (`/jobStation`,
   * `/jobStation/{id}`); always null from the `/admin` endpoints.
   */
  documentationUrlResolved?: string | null;
  /**
   * ASCII / punycode host derived from {@link documentationUrlResolved} for safe display.
   * Always null whenever {@link documentationUrlResolved} is null.
   */
  documentationUrlResolvedHost?: string | null;
  jobStationPersonalProtectiveEquipments: JobStationPersonalProtectiveEquipment[];
  jobStationDangerousEquipments: JobStationDangerousEquipment[];
  mCellEmployeeRoles: MCellEmployeeRole[];
  mCellEmployeeRoleIds: string[];
};

export type JobStationDto = {
  id: string;
  title: string;
  description?: string;
  mCellId: string;
  jobFunctionId: string;
  stationId: string;
  stationSequence: string;
  positionCount: number;
  crewingRateMaximum: number;
  ageRestriction: AgeRestriction;
  weightRestriction: WeightRestriction;
  /** See {@link JobStation.documentationUrlOverride}. */
  documentationUrlOverride?: string | null;
  /** See {@link JobStation.documentationUrlOverrideHost}. */
  documentationUrlOverrideHost?: string | null;
  /** See {@link JobStation.documentationUrlResolved}. */
  documentationUrlResolved?: string | null;
  /** See {@link JobStation.documentationUrlResolvedHost}. */
  documentationUrlResolvedHost?: string | null;
  mCellEmployeeRoles: MCellEmployeeRole[];
};

export type JobStationInfo = {
  id: string;
  title: string;
  description: string;
  mCellId: string;
  jobFunctionId: string;
  stationId: string;
  stationSequence: string;
  positionCount: number;
  crewingRateMaximum: number;
  ageRestriction: AgeRestriction;
  weightRestriction: WeightRestriction;
  /**
   * Optional manual override URL for this job station. Null/omitted means "no override —
   * fall back to the SharePoint-resolved URL". Empty strings should be normalized to `null`
   * by {@link normalizeJobStationDocumentationUrlInput} before sending.
   */
  documentationUrlOverride?: string | null;
  mCellEmployeeRoleIds: string[];
};

export type JobStationAssignmentDto = {
  mCellId: string;
  mCellEmployeeId: string;
  jobStationId: string;
  shift: Shift;
  assignTime: string;
  unassignTime?: string;
  ratePercent: number;
};

export type JobStationAssignmentToSet = {
  mCellEmployeeId: string;
  jobStationId: string;
};

export type JobStationAssignmentsSetter = {
  mCellId: string;
  shift: Shift;
  assignmentsToSet: JobStationAssignmentToSet[];
};

export type MCellEmployeeListDto = {
  id: string;
  employeeId: string;
  fullBadgeId: string;
  firstName: string;
  lastName: string;
  supervisorName: string;
  defaultMCellShift: Shift;
  active: boolean;
  isLocal: boolean;
  homeMCellId?: string;
  homeMCellCode?: string;
  inventoryCenterId: string;
  inventoryCenterCode?: string;
  departmentId?: string;
  departmentCode?: string;
  accountTypeId?: string;
  accountTypeDescription?: string;
  categoryId?: string;
  categoryDescription?: string;
  roleId?: string;
  roleTitle?: string;
};

export type EmployeeListDto = {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  badgeNumber: string;
  supervisorFullName: string;
  inventoryCenterCode: string;
  departmentCode: string;
};
